import React, { useEffect, useState } from 'react';

import { ProjectId } from '../../../models/Types';
import { useDispatch, useSelector } from 'react-redux';
import {
  archiveProjectDistributionList,
  fetchProjectDistribustionList,
} from '../actions';
import {
  getProjectDistributionLists,
  RootReducerState,
} from '../../../apps/main/rootReducer';
import { ProjectDistributionList } from '../../../models/ProjectDistributionList';
import Flex from '../../../components/Flex';
import { makePrioStyles } from '../../../theme/utils';
import ProjectDistributionListTable from './ProjectDistributionListTable';
import AddressesContextProvider from '../../mail/components/AddressesContextProvider';
import { useTranslation } from 'react-i18next';
import { Modal } from '@prio365/prio365-react-library';

const useStyles = makePrioStyles((theme) => ({
  root: {
    height: '100%',
    overflow: 'hidden',
    flex: 1,
  },
  content: {
    flex: 1,
    padding: theme.old.spacing.defaultPadding,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
}));

interface ProjectDistributionListPageProps {
  projectId: ProjectId;
}

export const ProjectDistributionListPage: React.FC<
  ProjectDistributionListPageProps
> = (props) => {
  //#region ------------------------------ Defaults
  const classes = useStyles();
  const { projectId } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [selectedTableItems, setSelectedTableItems] = useState<
    ProjectDistributionList[]
  >([]);

  const projectDistributionLists = useSelector<
    RootReducerState,
    ProjectDistributionList[]
  >((state) => getProjectDistributionLists(state, projectId));
  const [itemsToDelete, setItemsToDelete] = useState<ProjectDistributionList[]>(
    []
  );
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const handleOnDelete = (entries: ProjectDistributionList[]) => {
    setIsDeleteModalVisible(true);
    setItemsToDelete(entries);
  };
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    dispatch(fetchProjectDistribustionList(projectId));
  }, [dispatch, projectId]);
  //#endregion

  return (
    <AddressesContextProvider projectId={projectId}>
      <Flex.Row className={classes.root}>
        <div className={classes.content}>
          <ProjectDistributionListTable
            distributionLists={projectDistributionLists}
            projectId={projectId}
            selectedTableItems={selectedTableItems}
            setSelectedTableItems={setSelectedTableItems}
            showCreateButton={true}
            showDeleteButton={true}
            handleDeleteDistributionList={handleOnDelete}
          />
        </div>
        <Modal
          // className={classes.deleteModal}
          title={t('projects:projectDistributionList.modal.title')}
          visible={isDeleteModalVisible}
          onOk={() => {
            itemsToDelete.forEach((entry) => {
              dispatch(archiveProjectDistributionList(entry));
            });
            setSelectedTableItems([]);
            setIsDeleteModalVisible(false);
          }}
          onClose={() => {
            setIsDeleteModalVisible(false);
          }}
          okText={t('common:actions.delete')}
          cancelText={t('common:actions.cancel')}
          maskClosable={false}
        >
          <Flex.Column>
            <div>
              {t(
                `projects:projectDistributionList.modal.content${
                  itemsToDelete.length === 1 ? 'Singular' : 'Plural'
                }`
              )}
            </div>
            <ul>
              {itemsToDelete.map((entry) => (
                <li key={entry.projectDistributionListId}>{entry.name}</li>
              ))}
            </ul>
          </Flex.Column>
        </Modal>
      </Flex.Row>
    </AddressesContextProvider>
  );
};

export default ProjectDistributionListPage;
